import { useRef, useCallback } from "react";
import { gsap, isTouch, prefersReducedMotion } from "../../lib/motion";
import "./Button.css";

/**
 * Shared button/link primitive.
 *
 * variants — mirroring the reference site's jkit-button styles:
 *   solid   → orange pill (primary CTA "Download now")
 *   dark    → near-black pill (pricing "Choose Plan")
 *   ghost   → gradient hairline pill (header "LOG IN")
 *   circle  → round icon button (hero scroll cue)
 *
 * On fine pointers the button is magnetic: it eases toward the cursor and
 * springs back on leave. Disabled for touch and reduced-motion visitors.
 */
export default function Button({
  as = "button",
  variant = "solid",
  href,
  children,
  className = "",
  magnetic = true,
  strength = 0.32,
  onClick,
  ...rest
}) {
  const ref = useRef(null);
  const labelRef = useRef(null);
  const quickRef = useRef(null);

  const enable = magnetic && !isTouch() && !prefersReducedMotion();

  const handleEnter = useCallback(() => {
    if (!enable || !ref.current) return;
    quickRef.current = {
      x: gsap.quickTo(ref.current, "x", { duration: 0.45, ease: "power3.out" }),
      y: gsap.quickTo(ref.current, "y", { duration: 0.45, ease: "power3.out" }),
      lx: gsap.quickTo(labelRef.current, "x", { duration: 0.6, ease: "power3.out" }),
      ly: gsap.quickTo(labelRef.current, "y", { duration: 0.6, ease: "power3.out" }),
    };
  }, [enable]);

  const handleMove = useCallback(
    (e) => {
      const q = quickRef.current;
      if (!enable || !q || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      q.x(dx * strength);
      q.y(dy * strength);
      q.lx(dx * strength * 0.4);
      q.ly(dy * strength * 0.4);
    },
    [enable, strength]
  );

  const handleLeave = useCallback(() => {
    if (!enable || !ref.current) return;
    gsap.to(ref.current, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.4)" });
    gsap.to(labelRef.current, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.4)" });
    quickRef.current = null;
  }, [enable]);

  const Tag = href ? "a" : as;
  const external = href?.startsWith("http");

  return (
    <Tag
      ref={ref}
      href={href}
      onClick={onClick}
      className={`btn btn--${variant} ${className}`}
      data-cursor="hover"
      onPointerEnter={handleEnter}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
      {...rest}
    >
      <span className="btn__label" ref={labelRef}>
        {children}
      </span>
      <span className="btn__sheen" aria-hidden="true" />
    </Tag>
  );
}
